// @ts-nocheck

import { createSelector } from 'reselect';

import { BoostedSportsByCategories } from '../../constants/boostedOdds';
import { getCardModeFromProps, getMatchIdFromProps, getPathnameFromProps } from '../../selectors/_propsGetters_';
import {
  selectBasketIsLoading,
  selectBasketItems,
  selectBasketMode,
  selectBasketStatus,
} from '../../selectors/basket/basketPrimary.selector';
import { selectAreBigBoostedOddsCompact } from '../../selectors/boostedOdds.selector';
import {
  selectEntitiesMatchesEntityCategoryId,
  selectEntitiesMatchesEntityMainBetId,
  selectEntitiesMatchesEntityMatchStart,
  selectEntitiesMatchesEntitySportId,
  selectEntitiesMatchesEntityTitle,
  selectEntitiesMatchesEntityTournamentId,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import {
  makeMatchMainBetEntityOutcomesWithValueSelector,
  selectMatchImage,
  selectMatchMainBetEntityMMStatus,
  selectMatchMainBetEntityMultichoiceMMStatus,
  selectMatchMainBetEntityPreviousBoostedOdd,
  selectMatchViewMode,
} from '../../selectors/match.selector';
import { CardModes } from '../../types/common';
import { type Selector } from '../../types/selectors';
import { SportIds } from '../../types/sports';
import { getIsBasketItemSelected } from '../../utils/outcome';
import Routes from '../../utils/routes';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';

const makeBoostedOddCardPropsSelector = () => {
  const selectMatchMainBetOutcomesWithValue = makeMatchMainBetEntityOutcomesWithValueSelector();

  const selectIsCompact: Selector<boolean | undefined> = createSelector(
    [selectAreBigBoostedOddsCompact, getPathnameFromProps, getCardModeFromProps],
    (areBigBoostedOddsCompact, pathname, cardMode) => {
      if (cardMode === CardModes.BIG_BOOSTED) {
        return areBigBoostedOddsCompact;
      }

      if (cardMode === CardModes.BOOSTED) {
        return pathname === RoutesRedirectionFactory.getCatalogRoutePathname(SportIds.SPECIAL_ODDS);
      }
    },
  );

  const selectMatchMainBetFirstOutcomeWithValue = createSelector(
    selectMatchMainBetOutcomesWithValue,
    (outcomesWithValue) => outcomesWithValue[0],
  );

  const selectShowImage = createSelector([getPathnameFromProps, getCardModeFromProps], (pathname, cardMode) => {
    const isRouteCatalog = Routes.isCatalog(pathname);
    return !isRouteCatalog || cardMode === CardModes.BIG_BOOSTED;
  });

  const selectImage = createSelector([selectMatchImage, selectShowImage], (image, showImage) =>
    showImage ? image : null,
  );

  const selectNewValue = createSelector(
    [selectMatchMainBetFirstOutcomeWithValue],
    (matchMainBetFirstOutcomeWithValue) => matchMainBetFirstOutcomeWithValue?.value,
  );

  const selectOutcomeLabel = createSelector(
    [selectMatchMainBetFirstOutcomeWithValue],
    (matchMainBetFirstOutcomeWithValue) => matchMainBetFirstOutcomeWithValue?.label,
  );

  const selectOddId = createSelector(
    [selectMatchMainBetFirstOutcomeWithValue],
    (matchMainBetFirstOutcomeWithValue) => matchMainBetFirstOutcomeWithValue?.oddId,
  );

  const selectSelected = createSelector(
    [
      selectBasketMode,
      selectBasketStatus,
      selectBasketIsLoading,
      selectOddId,
      selectBasketItems,
      selectMatchViewMode,
      selectMatchMainBetEntityMMStatus,
      selectMatchMainBetEntityMultichoiceMMStatus,
    ],
    (basketMode, basketStatus, isLoading, oddId, basketItems, matchViewMode, betMMStatus, betMultichoiceMMStatus) =>
      getIsBasketItemSelected(
        basketMode,
        basketStatus,
        isLoading,
        oddId,
        basketItems,
        matchViewMode,
        false,
        betMMStatus,
        betMultichoiceMMStatus,
      ),
  );

  const selectSportIdByCategoryId = createSelector(
    selectEntitiesMatchesEntityCategoryId,
    (categoryId) => BoostedSportsByCategories[categoryId],
  );

  const selectTitle = createSelector([selectEntitiesMatchesEntityTitle], (title) => {
    const splittedTitle = title.split(':');
    const splittedTitleLength = splittedTitle.length;

    if (splittedTitleLength > 1) {
      return splittedTitle.slice(1).join('').trim();
    }

    return splittedTitle.join('').trim();
  });

  return createSelector(
    [
      selectEntitiesMatchesEntityMatchStart,
      selectEntitiesMatchesEntitySportId,
      selectEntitiesMatchesEntityCategoryId,
      selectEntitiesMatchesEntityTournamentId,
      selectImage,
      getMatchIdFromProps,
      selectTitle,
      selectMatchMainBetEntityPreviousBoostedOdd,
      selectNewValue,
      selectOutcomeLabel,
      selectEntitiesMatchesEntityMainBetId,
      selectOddId,
      selectSelected,
      selectSportIdByCategoryId,
      selectIsCompact,
    ],
    (
      startDate,
      sportId,
      categoryId,
      tournamentId,
      image,
      matchId,
      subTitle,
      oldValue,
      newValue,
      outcomeLabel,
      betId,
      oddId,
      selected,
      sportIdByCategory,
      isCompact,
    ) => ({
      startDate,
      sportId,
      tournamentId,
      categoryId,
      image,
      matchId,
      subTitle,
      oldValue,
      newValue,
      outcomeLabel,
      betId,
      oddId,
      selected,
      sportIdByCategory,
      isCompact,
    }),
  );
};

export default makeBoostedOddCardPropsSelector;
